import { toast } from "react-toastify";
import { SyndicationFeed } from "api/syndicationFeed";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { ADD_LISTINGS_LABEL } from "./constants";
import { useFeedStatus, useUpdateFeedSettings } from "./useFeedSettings";

interface FeedAutoAddCardProps {
  feed: SyndicationFeed;
  feedLabel: string;
}

export const FeedAutoAddCard = ({ feed, feedLabel }: FeedAutoAddCardProps) => {
  const { data, isLoading, isError } = useFeedStatus(feed);
  const updateSettings = useUpdateFeedSettings(feed);

  const checked = Boolean(data?.auto_add_listings);

  const handleChange = (value: boolean) =>
    updateSettings.mutate(value, {
      onSuccess: () =>
        toast.success(
          value
            ? `New listings will be added to the ${feedLabel} feed automatically`
            : `New listings will no longer be added to the ${feedLabel} feed automatically`
        )
    });

  return (
    <Card className="w-full p-6">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-content-1000">
            Automatically add new listings
          </h3>

          <Show if={isLoading}>
            <Loader />
          </Show>

          <Show if={!isLoading && isError}>
            <p className="shrink-0 text-sm text-content-400">
              Couldn&apos;t load this setting.
            </p>
          </Show>

          <Show if={!isLoading && !isError}>
            <Switch
              size={SwitchSize.SM}
              checked={checked}
              disabled={updateSettings.isPending}
              onChange={handleChange}
            />
          </Show>
        </div>

        <p className="text-sm text-content-400">
          When a new listing becomes available, add it to the {feedLabel} feed
          right away — no manual step required.
        </p>

        <Show if={Boolean(data) && !checked}>
          <p className="text-sm text-content-400">
            Off — new listings must be added from the {ADD_LISTINGS_LABEL}{" "}
            screen.
          </p>
        </Show>
      </div>
    </Card>
  );
};
